// Manual pointer-based card drag. Native HTML5 DnD is out: its ghost is a
// washed-out OS snapshot that cannot be styled or animated. Here a full
// opacity tilted clone rides the cursor with a small lerp lag, lanes are
// hit-tested every frame, and a release outside a lane flies the clone back
// to its slot.

const DRAG_THRESHOLD_PX = 5;
// how fast the clone catches up with the cursor, per frame (1 = instant)
const FOLLOW_LERP = 0.35;
const EDGE_SCROLL_ZONE_PX = 60;
const EDGE_SCROLL_STEP_PX = 12;

export type CardDragCallbacks = {
  // threshold passed, clone created - the drag is real now
  onLift: () => void;
  // drop lane under the pointer changed (null = not over any lane)
  onHover: (status: string | null) => void;
  // drag ended; status = lane it landed on (null = cancelled / no lane)
  // before = id of the card the dragged one should land in front of
  // (null = end of the lane)
  onFinish: (
    status: string | null,
    at: { x: number; y: number },
    before: string | null,
  ) => void;
};

// call from pointerdown on a card; does nothing visible until the pointer
// travels past the threshold, so plain clicks stay clicks
export function startCardDrag(
  down: PointerEvent,
  cardEl: HTMLElement,
  cb: CardDragCallbacks,
): void {
  const rect = cardEl.getBoundingClientRect();
  const grabX = down.clientX - rect.left;
  const grabY = down.clientY - rect.top;
  let pointerX = down.clientX;
  let pointerY = down.clientY;
  let ghost: HTMLElement | null = null;
  let ghostX = rect.left;
  let ghostY = rect.top;
  let hover: string | null = null;
  let before: string | null = null;
  let lifted = false;
  let raf = 0;
  let indicator: HTMLElement | null = null;

  // ghost has pointer-events none, so elementFromPoint sees through it
  const laneAt = (x: number, y: number): HTMLElement | null =>
    (document.elementFromPoint(x, y)?.closest('[data-drop-status]') as HTMLElement | null) ??
    null;

  const hideIndicator = (): void => {
    indicator?.remove();
    indicator = null;
  };

  const showIndicator = (top: number, left: number, width: number): void => {
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'drop-indicator';
      document.body.appendChild(indicator);
    }
    indicator.style.top = `${top}px`;
    indicator.style.left = `${left}px`;
    indicator.style.width = `${width}px`;
  };

  // find the slot under the pointer. Cards carry data-card-id and
  // data-card-index (position in the lane's ordered list). Backlog deals its
  // list into two stacks, so the slot is looked up in the stack nearest to
  // the pointer and the index maps back to the list order.
  const findSlot = (lane: HTMLElement, x: number, y: number): void => {
    // append lanes take the card without a position - no slot to pick
    if (lane.getAttribute('data-drop-mode') === 'append') {
      before = null;
      hideIndicator();
      return;
    }
    let cards = [...lane.querySelectorAll<HTMLElement>('[data-card-id]')].filter(
      (el) => el !== cardEl,
    );
    // grouped lanes keep own/foreign tickets apart - a slot only makes sense
    // inside the dragged card's group, so the line never points at a spot
    // the card cannot actually land in
    if (lane.getAttribute('data-drop-mode') === 'grouped') {
      const group = cardEl.getAttribute('data-card-group');
      cards = cards.filter((el) => el.getAttribute('data-card-group') === group);
    }
    if (cards.length === 0) {
      before = null;
      hideIndicator();
      return;
    }
    // bucket cards into vertical stacks by left edge
    const stacks = new Map<number, HTMLElement[]>();
    for (const el of cards) {
      const left = Math.round(el.getBoundingClientRect().left);
      const stack = stacks.get(left);
      if (stack) stack.push(el);
      else stacks.set(left, [el]);
    }
    let stack: HTMLElement[] = [];
    let bestDist = Infinity;
    for (const els of stacks.values()) {
      const first = els[0];
      if (!first) continue;
      const r = first.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - x);
      if (dist < bestDist) {
        bestDist = dist;
        stack = els;
      }
    }
    for (const el of stack) {
      const r = el.getBoundingClientRect();
      if (y < r.top + r.height / 2) {
        before = el.getAttribute('data-card-id');
        showIndicator(r.top - 6, r.left, r.width);
        return;
      }
    }
    // below the whole stack - logically right after its last card
    const last = stack[stack.length - 1];
    if (!last) {
      before = null;
      hideIndicator();
      return;
    }
    const lastRect = last.getBoundingClientRect();
    const lastIndex = Number(last.getAttribute('data-card-index'));
    const successor = lane.querySelector<HTMLElement>(`[data-card-index="${lastIndex + 1}"]`);
    before = successor?.getAttribute('data-card-id') ?? null;
    showIndicator(lastRect.bottom + 4, lastRect.left, lastRect.width);
  };

  const lift = (): void => {
    lifted = true;
    ghost = cardEl.cloneNode(true) as HTMLElement;
    ghost.classList.add('drag-ghost');
    ghost.style.width = `${rect.width}px`;
    ghost.style.transform = `translate(${ghostX}px, ${ghostY}px) rotate(4deg)`;
    document.body.appendChild(ghost);
    document.body.classList.add('drag-active');
    // kill any half-made text selection from before the threshold
    window.getSelection()?.removeAllRanges();
    cb.onLift();
    raf = requestAnimationFrame(tick);
  };

  const tick = (): void => {
    ghostX += (pointerX - grabX - ghostX) * FOLLOW_LERP;
    ghostY += (pointerY - grabY - ghostY) * FOLLOW_LERP;
    if (ghost) ghost.style.transform = `translate(${ghostX}px, ${ghostY}px) rotate(4deg)`;
    // page auto-scroll near the viewport edges so far lanes are reachable
    if (pointerY < EDGE_SCROLL_ZONE_PX) window.scrollBy(0, -EDGE_SCROLL_STEP_PX);
    else if (pointerY > window.innerHeight - EDGE_SCROLL_ZONE_PX)
      window.scrollBy(0, EDGE_SCROLL_STEP_PX);
    // hit-test in the loop, not in pointermove - lanes also move on scroll
    const lane = laneAt(pointerX, pointerY);
    const next = lane?.getAttribute('data-drop-status') ?? null;
    if (next !== hover) {
      hover = next;
      cb.onHover(next);
    }
    if (lane) {
      findSlot(lane, pointerX, pointerY);
    } else {
      before = null;
      hideIndicator();
    }
    raf = requestAnimationFrame(tick);
  };

  const onMove = (e: PointerEvent): void => {
    pointerX = e.clientX;
    pointerY = e.clientY;
    if (
      !lifted &&
      Math.hypot(e.clientX - down.clientX, e.clientY - down.clientY) >= DRAG_THRESHOLD_PX
    ) {
      lift();
    }
  };

  const cleanup = (): void => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onCancel);
    window.removeEventListener('keydown', onKey);
    cancelAnimationFrame(raf);
    hideIndicator();
    document.body.classList.remove('drag-active');
  };

  // after a real drag the browser still fires a click on the source card -
  // trap it once so it does not toggle/collapse anything
  const swallowNextClick = (): void => {
    const swallow = (e: MouseEvent): void => {
      e.stopPropagation();
      e.preventDefault();
    };
    window.addEventListener('click', swallow, true);
    // the click (if any) comes synchronously after pointerup; drop the trap
    window.setTimeout(() => window.removeEventListener('click', swallow, true), 0);
  };

  const finish = (dropped: boolean): void => {
    cleanup();
    if (!lifted) return; // plain click, nothing started
    swallowNextClick();
    const target = dropped ? hover : null;
    const g = ghost;
    if (g) {
      if (target) {
        // small pop at the drop point
        g.style.transition = 'transform 0.15s ease, opacity 0.15s ease';
        g.style.opacity = '0';
        g.style.transform = `translate(${ghostX}px, ${ghostY}px) scale(0.92)`;
        window.setTimeout(() => g.remove(), 160);
      } else {
        // no lane - fly back to the slot it was lifted from
        const home = cardEl.getBoundingClientRect();
        g.style.transition = 'transform 0.25s ease';
        g.style.transform = `translate(${home.left}px, ${home.top}px)`;
        window.setTimeout(() => g.remove(), 260);
      }
    }
    cb.onFinish(target, { x: pointerX, y: pointerY }, target ? before : null);
  };

  const onUp = (e: PointerEvent): void => {
    pointerX = e.clientX;
    pointerY = e.clientY;
    finish(true);
  };
  const onCancel = (): void => finish(false);
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') finish(false);
  };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
  window.addEventListener('keydown', onKey);
}
